import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  AddressRequestModel,
  ContactRequestModel,
  CreateOrderModeEnum,
  CreateOrderRequestModel,
  PageViewModel,
  PartyRequestModel,
  PartyTypeEnum,
} from "src/domain/client";
import { TOAST_TYPE } from "src/domain/extentions/toast";
import { BrandService } from "src/services/brand.service";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ErrorReporterService } from "src/services/error-reporter.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { OrderService } from "src/services/order.service";
import { PageService } from "src/services/page.service";
import { ShoppingCartService } from "src/services/shopping-cart.service";
import { ThemeService } from "src/services/theme.service";
import { ToastService } from "src/services/toast.service";

@Component({
  selector: "vw-checkout-page",
  templateUrl: "./checkout-page.component.html",
})
export class CheckoutPageComponent implements OnInit {
  constructor(
    public modelAndDealerService: ModelAndDealerService,
    public shoppingCartService: ShoppingCartService,
    public themeService: ThemeService,
    private pageService: PageService,
    private brandService: BrandService,
    private toastService: ToastService,
    private errorReporter: ErrorReporterService,
    private translateService: TranslateService,
    private orderService: OrderService,
    private channelRequestService: ChannelRequestService,
    private router: Router,
  ) {}

  page?: PageViewModel;
  submitting = false;
  savingCustomer = false;

  customerInformationForm = new FormGroup({
    firstName: new FormControl("", [
      Validators.required,
      Validators.maxLength(100),
    ]),
    lastName: new FormControl("", [
      Validators.required,
      Validators.maxLength(100),
    ]),
    address: new FormControl("", [
      Validators.required,
      Validators.maxLength(255),
    ]),
    postalCode: new FormControl("", [
      Validators.required,
      Validators.pattern("[0-9]{5}"),
    ]),
    city: new FormControl("", [Validators.required, Validators.maxLength(100)]),
    mobilePhone: new FormControl("", [
      Validators.required,
      Validators.pattern("^(07[0236]{1})([0-9]{7})$"),
    ]),
    email: new FormControl("", [Validators.required]),
    contactCustomer: new FormControl(false),
    orderMessage: new FormControl(""),
    accept: new FormControl(false, [Validators.required]),
  });

  ngOnInit(): void {
    this.getCheckoutPage();
    this.patchCustomerInformationForm();
  }

  saveCustomerInformation(): void {
    if (!this.customerInformationForm.valid) {
      this.customerInformationForm.markAllAsTouched();
      return;
    }

    const requestModel = this.mapCustomerPartyRequestModel();

    this.savingCustomer = true;
    this.channelRequestService
      .updateChannelRequestCustomerParty(requestModel)
      .then(() => {
        this.toastService.createToast(
          this.translateService.instant("successMessages.customerPartySaved"),
          TOAST_TYPE.SUCCESS,
        );
      })
      .catch(this.errorReporter.handle)
      .finally(() => {
        this.savingCustomer = false;
      });
  }

  saveCustomerInformationAndCreateOrder(): void {
    if (!this.customerInformationForm.valid) {
      this.customerInformationForm.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const customerPartyRequestModel = this.mapCustomerPartyRequestModel();

    this.channelRequestService
      .updateChannelRequestCustomerParty(customerPartyRequestModel)
      .then(() => {
        this.createOrder();
      })
      .catch((err) => {
        this.submitting = false;
        this.errorReporter.handle(err);
      });
  }

  //////////

  private getCheckoutPage(): void {
    this.pageService
      .getCheckoutPage(this.brandService.brandId)
      .then((startPage) => (this.page = startPage))
      .catch(this.errorReporter.handle);
  }

  private createOrder(): void {
    const requestModel = this.mapRequestModel();
    const shoppingCartId =
      this.shoppingCartService.shoppingCart?.shoppingCartId;
    this.orderService
      .createOrder(requestModel)
      .then(() => {
        this.router.navigate([
          this.themeService.brand,
          "thank-you",
          shoppingCartId,
        ]);
      })
      .catch((err) => {
        this.submitting = false;
        this.errorReporter.handle(err);
      });
  }

  private mapRequestModel(): CreateOrderRequestModel {
    const formValue = this.customerInformationForm.value;
    const shoppingCartId =
      this.shoppingCartService.shoppingCart?.shoppingCartId;
    if (!shoppingCartId) throw new Error("shoppingCartId is missing");

    const campaignCodeId =
      (this.shoppingCartService.campaignCodeId as number) > 0
        ? this.shoppingCartService.campaignCodeId
        : undefined;

    return new CreateOrderRequestModel({
      contactCustomer: formValue.contactCustomer ?? undefined,
      paymentMethodTypeId: 2, // TODO
      shoppingCartId,
      orderMessage: formValue.orderMessage ?? undefined,
      createOrderMode: CreateOrderModeEnum.NoPassingControl,
      campaignCodeId: campaignCodeId,
    });
  }

  private mapCustomerPartyRequestModel(): PartyRequestModel {
    const customerInfo = this.customerInformationForm.value;
    if (customerInfo.firstName == null) {
      throw new Error("No first name provided");
    }
    const address =
      customerInfo.address != null &&
      customerInfo.city != null &&
      customerInfo.postalCode != null
        ? new AddressRequestModel({
            address1: customerInfo.address,
            city: customerInfo.city,
            postalCode: customerInfo.postalCode,
          })
        : undefined;
    return new PartyRequestModel({
      name1: customerInfo.firstName,
      name2: customerInfo.lastName ?? undefined,
      partyType: PartyTypeEnum.CustomerConsumer,
      contact: new ContactRequestModel({
        email: customerInfo.email ?? undefined,
        mobilePhone: customerInfo.mobilePhone ?? undefined,
      }),
      address,
    });
  }

  private patchCustomerInformationForm(): void {
    const customerParty =
      (this.channelRequestService.channelRequest &&
        this.channelRequestService.channelRequest.parties &&
        this.channelRequestService.channelRequest.parties.length &&
        this.channelRequestService.channelRequest.parties.find(
          (_) => _.partyTypeName !== PartyTypeEnum.Dealer,
        )) ||
      null;

    if (!customerParty) {
      return;
    }

    this.customerInformationForm.patchValue({
      firstName: customerParty.name1,
      lastName: customerParty.name2,
      address: (customerParty.address && customerParty.address.address1) || "",
      postalCode:
        (customerParty.address && customerParty.address.postalCode) || "",
      city: (customerParty.address && customerParty.address.city) || "",
      mobilePhone: customerParty.contact && customerParty.contact.mobilePhone,
      email: (customerParty.contact && customerParty.contact.email) || "",
    });
  }
}
